import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  locale?: string;
  noindex?: boolean;
}

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url,
  type = 'website',
  locale,
  noindex = false
}: SEOProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Get site title based on language
  const siteTitle = t('site.title');
  const defaultDescription = t('site.description');
  const defaultKeywords = t('site.keywords');
  const defaultImage = 'https://res.cloudinary.com/djimcqajg/image/upload/v1741340483/vefsida-logo-v2_y5imtb.svg';

  // Get domain based on language
  const domains = {
    is: 'https://vefsida.is',
    en: 'https://vefsida.io',
    es: 'https://vefsida.es',
    da: 'https://vefsida.dk'
  };
  const defaultUrl = domains[currentLanguage as keyof typeof domains] || domains.is;

  // Map language codes to locales
  const locales = {
    is: 'is-IS',
    en: 'en-US',
    es: 'es-ES',
    da: 'da-DK'
  };
  const defaultLocale = locales[currentLanguage as keyof typeof locales] || locales.is;

  // Determine the full title based on whether a specific title was provided
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;

  return (
    <Helmet>
      {/* Basic meta tags */}
      <html lang={currentLanguage} />
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />

      {/* Language alternates */}
      {Object.entries(domains).map(([lang, domain]) => (
        <link 
          key={lang}
          rel="alternate" 
          hrefLang={lang}
          href={`${domain}${url || ''}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={domains.is} />

      {/* Robots meta */}
      <meta 
        name="robots" 
        content={noindex ? 'noindex, nofollow' : 'index, follow'} 
      />

      {/* OpenGraph meta tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:url" content={url || defaultUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content={locale || defaultLocale} />

      {/* Twitter meta tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={image || defaultImage} />

      {/* Additional meta tags */}
      <meta name="language" content={currentLanguage} />
      <meta name="author" content={siteTitle} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="geo.region" content="IS" />
      <meta name="geo.placename" content="Reykjavík" />
    </Helmet>
  );
};

export default SEO;